import React, { useLayoutEffect, useRef, useState, useCallback } from 'react';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import {
  ContentWrapper,
  HighlightsContainer,
  CarouselHorizontalWrapper,
  HandCraftedDesignsBlock,
  HighlightsBlock,
  StyledDotIndicator,
  StyledCarouselDot,
  MobileHighlightSectionWrapper,
  DesktopHighlightSectionWrapper,
  DesignPlaceholderCloudImage,
  DesignImageBox,
  DesignImagesWrapper,
  DesignImageBoxFirstItem,
  HeadingWrpper,
  HeadingContainer,
  TitleText,
  SubTitleText,
  TitleTextMobile,
  SubTitleTextMobile,
} from './CustomizeThemeSection.styles';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import ScrollMagic from 'scrollmagic';
import { gsap } from 'gsap';
import { useTheme } from '@styledComponents';
import { CarouselIndicator } from '@components/Carousel';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { Column, Columns } from '@components/Columns';
import { useTranslation } from 'react-i18next';
import { JoykitIconVariant } from '@components/JoykitIcon';

// const FRAMES_PER_STEP = [0, 90, 250, 400, 550];

export const CustomizeThemeSection: React.FC = () => {
  const isMobile = useThemeMediaQuery('below_sm');
  const [activeDot, setActiveDot] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const sectionId = 'Design_Features';
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const theme = useTheme();

  const { t } = useTranslation('weddingWebsitePage');

  const customizeThemeData = t('sections.customizeTheme');

  const renderHighlightsBlocks = () => {
    return customizeThemeData.highlights.map((highlight, index) => {
      if (index === 0) {
        return (
          <HandCraftedDesignsBlock
            icon={highlight.icon as JoykitIconVariant}
            iconColor="accentBlue4"
            key={index}
            titleTagName="h2"
            titleVariant="featureHed17"
            titleColor="neutra6"
            bodyVariant="featureCopy17"
            bodyColor="neutra5"
            title={highlight.title}
            body={highlight.description}
          />
        );
      }
      return (
        <HighlightsBlock
          icon={highlight.icon as JoykitIconVariant}
          iconColor="accentBlue4"
          key={index}
          titleTagName="h2"
          titleVariant="featureHed17"
          titleColor="neutra6"
          bodyVariant="featureCopy17"
          bodyColor="neutra5"
          title={highlight.title}
          body={highlight.description}
        />
      );
    });
  };

  useLayoutEffect(() => {
    let customizeThemeScene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let customizeThemeSectionTimeline: ReturnType<typeof gsap.timeline>;
    const contentWrapperEl = contentWrapperRef.current;
    const highlightTextElements = contentWrapperEl?.querySelectorAll('.customize-theme-highlights-block');
    const designImagesElements = contentWrapperEl?.querySelectorAll('.customized-theme-image-block');
    if (scrollController && isMounted && contentWrapperEl && highlightTextElements && designImagesElements) {
      const translateY = '-150vh';
      const translateLastTextY = '-70vh';
      const textDuration = 0.25;
      const textPosition = '-=0.1';
      const initCustomizeThemeTimeline = () => {
        customizeThemeSectionTimeline = gsap.timeline({ paused: true });
        if (!isMobile) {
          customizeThemeSectionTimeline.to(highlightTextElements[0], {
            y: '-=120vh',
            duration: textDuration,
          });

          customizeThemeSectionTimeline.to(
            highlightTextElements[1],
            {
              y: translateY,
              duration: textDuration,
              onStart: function () {
                designImagesElements[0].classList.add('design-image-hide');
                designImagesElements[1].classList.add('design-image-visible');
              },
              onReverseComplete: function () {
                designImagesElements[1].classList.remove('design-image-visible');
                designImagesElements[0].classList.remove('design-image-hide');
              },
            },
            textPosition
          );
          customizeThemeSectionTimeline.to(
            highlightTextElements[2],
            {
              y: translateY,
              duration: textDuration,
              onStart: function () {
                designImagesElements[1].classList.remove('design-image-visible');
                designImagesElements[2].classList.add('design-image-visible');
              },
              onReverseComplete: function () {
                designImagesElements[2].classList.remove('design-image-visible');
                designImagesElements[1].classList.add('design-image-visible');
              },
            },
            textPosition
          );
          customizeThemeSectionTimeline.to(
            highlightTextElements[3],
            {
              y: translateLastTextY,
              duration: textDuration,
              onStart: function () {
                designImagesElements[2].classList.remove('design-image-visible');
                designImagesElements[3].classList.add('design-image-visible');
              },
              onReverseComplete: function () {
                designImagesElements[3].classList.remove('design-image-visible');
                designImagesElements[2].classList.add('design-image-visible');
              },
            },
            textPosition
          );
        }
        customizeThemeSectionTimeline.to(contentWrapperRef.current, { duration: 0.1 }, 0);
      };

      const initCustomizeThemeScene = () => {
        customizeThemeScene = new ScrollMagic.Scene({
          triggerElement: `#${sectionId}`,
          triggerHook: 0,
          duration: isMobile ? '50%' : '500%',
        }).addTo(scrollController);
        if (!isMobile) {
          customizeThemeScene.setPin(`#${sectionId}`);
        }
        customizeThemeScene.on('progress', (event: ScrollMagic.SceneProgressEvent) => {
          customizeThemeSectionTimeline?.progress(event.progress * 0.8);
          setIsScrolling(event.progress > 0);
        });
      };
      initCustomizeThemeTimeline();
      initCustomizeThemeScene();
    }
    const clearScene = () => {
      if (customizeThemeSectionTimeline) {
        customizeThemeSectionTimeline.progress(0);
        customizeThemeSectionTimeline.kill();
      }

      if (customizeThemeScene) {
        customizeThemeScene.destroy(true);
      }
    };

    return () => {
      clearScene();
    };
  }, [isMounted, scrollController, contentWrapperRef, theme, isMobile]);

  const setActiveCarousel = useCallback(
    (index: number) => {
      const contentWrapperEl = contentWrapperRef.current;
      const designImagesElements = contentWrapperEl?.querySelectorAll('.customized-theme-image-block');
      if (designImagesElements) {
        if (activeDot === 0 && index === 1) {
          designImagesElements[activeDot].classList.add('design-image-hide');
          designImagesElements[index].classList.add('design-image-visible');
        } else if (activeDot === 1 && index === 0) {
          designImagesElements[activeDot].classList.remove('design-image-visible');
          designImagesElements[index].classList.remove('design-image-hide');
        } else {
          designImagesElements[activeDot].classList.remove('design-image-visible');
          designImagesElements[index].classList.add('design-image-visible');
        }
      }
      setActiveDot(index);
    },
    [setActiveDot, activeDot]
  );

  const renderActiveDot = () => {
    return <StyledCarouselDot isActive={true} />;
  };

  const renderInactiveDot = () => {
    return <StyledCarouselDot isActive={false} />;
  };

  const [isVisible, setIsVisible] = useState(false);
  return (
    <ContentWrapper id={sectionId} ref={contentWrapperRef} backgroundColor="mono7" marginTop={[8, 10]} marginBottom={[0, 10]}>
      <LazyLoadWrapper beforeLoad={() => setIsVisible(true)}>
        <div />
      </LazyLoadWrapper>
      <HeadingWrpper isScrolling={isScrolling}>
        <HeadingContainer>
          <Columns fluid verticalAlign={'center'}>
            <Column offset={1} width={4} mobile="full" css={{ display: 'flex' }}>
              <TitleText variant="calloutHed32" tagName="h2" color="neutra6">
                {customizeThemeData.customizeText}
              </TitleText>
              <TitleTextMobile variant="sectionEyeBrow24" tagName="h2" color="neutra6">
                {customizeThemeData.customizeText}
              </TitleTextMobile>
            </Column>
            <Column offset={1} width={6} mobile="full" css={{ display: 'flex' }}>
              <SubTitleText variant="dek24" tagName="p" color="neutra5">
                {customizeThemeData.unleashText}
              </SubTitleText>
              <SubTitleTextMobile variant="featureCopy17" tagName="p" color="neutra5">
                {customizeThemeData.unleashTextMobile}
              </SubTitleTextMobile>
            </Column>
          </Columns>
        </HeadingContainer>
      </HeadingWrpper>
      <DesignImagesWrapper paddingTop={[8, 12, 15]} paddingBottom={[0, 10, 12]}>
        {['design-theme.png', 'design-color.png', 'design-font.png', 'design-code.png'].map((item, index) => {
          if (index === 0) {
            return (
              <DesignImageBoxFirstItem key={item}>
                <DesignPlaceholderCloudImage
                  src={`wedding-website/design-features/${item}`}
                  isMainSrcShown={isVisible}
                  optimization="default"
                />
              </DesignImageBoxFirstItem>
            );
          } else {
            return (
              <DesignImageBox key={item}>
                <DesignPlaceholderCloudImage
                  src={`wedding-website/design-features/${item}`}
                  isMainSrcShown={isVisible}
                  optimization="default"
                />
              </DesignImageBox>
            );
          }
        })}
      </DesignImagesWrapper>
      <HighlightsContainer>
        <DesktopHighlightSectionWrapper>{renderHighlightsBlocks()}</DesktopHighlightSectionWrapper>
        <MobileHighlightSectionWrapper>
          <CarouselHorizontalWrapper
            slideIndex={activeDot}
            withoutControls={true}
            speed={500}
            beforeSlide={(currentSlide: number, endSlide: number) => {
              setActiveCarousel(endSlide);
            }}
          >
            {renderHighlightsBlocks()}
          </CarouselHorizontalWrapper>
          <StyledDotIndicator>
            <CarouselIndicator
              quantity={customizeThemeData.highlights.length}
              dotsPerPage={customizeThemeData.highlights.length}
              infinite={false}
              activeIndex={activeDot}
              onDotSelected={setActiveCarousel}
              renderDot={renderInactiveDot()}
              renderDotActive={renderActiveDot()}
            />
          </StyledDotIndicator>
        </MobileHighlightSectionWrapper>
      </HighlightsContainer>
    </ContentWrapper>
  );
};
